import { useCallback, useRef, type RefObject } from "react"

import { CONVERSATION_SEARCH_TYPE } from "../types"

interface UseConversationsVisualizerSelectionParams {
  clearAllFilters: () => void
  clusterFilterIds: string[] | null
  hasActiveFilters: boolean
  searchQuery: string
  searchType: (typeof CONVERSATION_SEARCH_TYPE)[keyof typeof CONVERSATION_SEARCH_TYPE]
  setActiveItemId: (itemId: string) => void
  setClusterFilterIds: (ids: string[] | null) => void
  setSearchQuery: (query: string) => void
  setSearchType: (
    searchType: (typeof CONVERSATION_SEARCH_TYPE)[keyof typeof CONVERSATION_SEARCH_TYPE],
  ) => void
  setSelectedKeys: (keys: Set<string>) => void
  tableScrollContainerRef: RefObject<HTMLDivElement | null>
}

/**
 * Drives the two ways the visualizer can narrow the conversations table: clicking one
 * conversation dot filters the table down to exactly that conversation (via the
 * backend's SIMULATION_OUTPUT_ID exact-match search), and clicking a cluster/bucket
 * filters it down to that cluster's full membership (via an explicit id-list filter).
 * Both replace whatever filter the other last applied -- they're mutually exclusive.
 *
 * This used to page through the table's already-active query looking for the clicked
 * conversation and scroll to it, which silently did nothing whenever the target fell
 * outside whatever filters (agent, metadata, search) the table already had active --
 * there was no way to tell the two apart from a plain "nothing happened" click. The
 * keyset request ANDs simulation_output_ids with every other active filter, so setting
 * an explicit filter alone does not guarantee the target is reachable -- an agent/date/
 * tag/metadata/metric filter that excludes it still empties the table. Both handlers
 * clear those other filters too, so the visualizer's own filter is the only constraint
 * left and the target is actually guaranteed reachable.
 *
 * Setting searchQuery/clusterFilterIds also drives an existing effect in the table that
 * clears selectedKeys whenever a filter changes -- that effect must skip the one change
 * this hook makes, since it sets a selection alongside the filter on purpose. An internal
 * flag flips true right before that setter call, and the returned
 * consumeSkipNextSelectionReset lets the effect check-and-clear it in one call instead
 * of resetting the selection.
 *
 * That flag only gets consumed because the effect's dependencies actually change value,
 * scheduling a run of it. Re-clicking the conversation the table is already exactly
 * filtered to, with no other filter active to clear, would set clusterFilterIds/
 * searchType/searchQuery to the same values they already hold, so the effect would
 * never run to consume the flag -- it would stay armed and wrongly protect the next
 * unrelated filter change from clearing the selection. So handleVisualizerConversationClick
 * only arms the flag when something will actually change, including whether there are
 * other filters left to clear. handleVisualizerClusterFilter needs no such check: it
 * always passes a freshly mapped array, which is a new reference every call, so
 * clusterFilterIds always changes and the effect always runs.
 */
export function useConversationsVisualizerSelection({
  clearAllFilters,
  clusterFilterIds,
  hasActiveFilters,
  searchQuery,
  searchType,
  setActiveItemId,
  setClusterFilterIds,
  setSearchQuery,
  setSearchType,
  setSelectedKeys,
  tableScrollContainerRef,
}: UseConversationsVisualizerSelectionParams) {
  const skipNextSelectionResetRef = useRef(false)

  const consumeSkipNextSelectionReset = useCallback(() => {
    if (!skipNextSelectionResetRef.current) {
      return false
    }
    skipNextSelectionResetRef.current = false
    return true
  }, [])

  const scrollTableToTop = useCallback(() => {
    tableScrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" })
  }, [tableScrollContainerRef])

  const handleVisualizerConversationClick = useCallback(
    (conversationId: string) => {
      const isAlreadyApplied =
        !hasActiveFilters &&
        clusterFilterIds === null &&
        searchType === CONVERSATION_SEARCH_TYPE.SIMULATION_OUTPUT_ID &&
        searchQuery === conversationId
      if (!isAlreadyApplied) {
        skipNextSelectionResetRef.current = true
      }
      clearAllFilters()
      setClusterFilterIds(null)
      setSearchType(CONVERSATION_SEARCH_TYPE.SIMULATION_OUTPUT_ID)
      setSearchQuery(conversationId)
      setSelectedKeys(new Set([conversationId]))
      setActiveItemId(conversationId)
      scrollTableToTop()
    },
    [
      clearAllFilters,
      clusterFilterIds,
      hasActiveFilters,
      scrollTableToTop,
      searchQuery,
      searchType,
      setActiveItemId,
      setClusterFilterIds,
      setSearchQuery,
      setSearchType,
      setSelectedKeys,
    ],
  )

  const handleVisualizerClusterFilter = useCallback(
    (simulationOutputIds: string[]) => {
      skipNextSelectionResetRef.current = true
      clearAllFilters()
      setSearchType(CONVERSATION_SEARCH_TYPE.ALL_FIELDS)
      setSearchQuery("")
      setClusterFilterIds(simulationOutputIds)
      setSelectedKeys(new Set())
      scrollTableToTop()
    },
    [
      clearAllFilters,
      scrollTableToTop,
      setClusterFilterIds,
      setSearchQuery,
      setSearchType,
      setSelectedKeys,
    ],
  )

  return {
    consumeSkipNextSelectionReset,
    handleVisualizerClusterFilter,
    handleVisualizerConversationClick,
  }
}
